// The picture of a stack of cubes, in 3D or as its flat views. Plain React
// SVG, like practice-graphs.tsx. The top of a cube is lightest and its
// right side darkest, so the shape reads without any words.

import {
  CUBE_CAPTION_SIZE,
  cubeStackPicture,
  describeCubeStack,
  type CubePoint,
  type CubeStackSpec,
} from './practice-cube-stack-data';

const INK = '#2d4a3e';
const FLOOR = '#9fb5a7';
const SHADE = { top: '#eef3ee', front: '#cfe0d4', right: '#a9c4b2' };

const fixed = (value: number) => value.toFixed(2);
const path = (points: CubePoint[]) =>
  `${points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${fixed(point.x)} ${fixed(point.y)}`).join(' ')} Z`;

// The arrow's line stops short of its tip so the head covers it; the head
// is turned to point the way the arrow goes.
function arrowParts(from: CubePoint, to: CubePoint) {
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  const way = { x: (to.x - from.x) / length, y: (to.y - from.y) / length };
  const at = (back: number, aside: number) => ({
    x: to.x - way.x * back - way.y * aside,
    y: to.y - way.y * back + way.x * aside,
  });
  return { end: at(4, 0), head: [at(7, -5), to, at(7, 5)] };
}

export function CubeStackView({ stack }: { stack: CubeStackSpec }) {
  const picture = cubeStackPicture(stack);
  const { box, arrow } = picture;
  const parts = arrow && arrowParts(arrow.from, arrow.to);

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeCubeStack(stack)}
        viewBox={`${fixed(box.x)} ${fixed(box.y)} ${fixed(box.width)} ${fixed(box.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(box.width * 1.4))}px` }}
      >
        {picture.floor.map((cell, index) => (
          <path
            key={index}
            data-part="floor"
            d={path(cell)}
            fill="none"
            stroke={FLOOR}
            strokeWidth="1"
          />
        ))}
        {picture.faces.map((face, index) => (
          <path
            key={index}
            data-face={face.side}
            d={path(face.points)}
            fill={SHADE[face.side]}
            stroke={INK}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        ))}
        {arrow && parts && (
          <g data-part="front-arrow">
            <line
              x1={fixed(arrow.from.x)}
              y1={fixed(arrow.from.y)}
              x2={fixed(parts.end.x)}
              y2={fixed(parts.end.y)}
              stroke={INK}
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path d={path(parts.head)} fill={INK} />
            <text
              x={fixed(arrow.word.x)}
              y={fixed(arrow.word.y)}
              dy="0.35em"
              textAnchor="middle"
              fill={INK}
              fontSize={CUBE_CAPTION_SIZE}
              fontWeight="700"
            >
              front
            </text>
          </g>
        )}
        {picture.captions.map((caption) => (
          <text
            key={caption.text}
            data-caption="true"
            x={fixed(caption.x)}
            y={fixed(caption.y)}
            dy="0.35em"
            textAnchor="middle"
            fill={INK}
            fontSize={CUBE_CAPTION_SIZE}
            fontWeight="700"
          >
            {caption.text}
          </text>
        ))}
      </svg>
    </figure>
  );
}
